import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Grid2,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "../../../../api/axiosConfig";
import AddSessionModal from "./AddSessionModal";
import DeleteSessionModal from "./DeleteSessionModal"; // Import delete modal

export default function AddSession() {
  const { id: scheduleId } = useParams();
  const location = useLocation();
  // Try to get campId from router state (preferred), then query param, then env var fallback
  const stateCampId = location && location.state && location.state.campId;
  const searchParams = new URLSearchParams(location.search);
  const campIdFromQuery = searchParams.get("campId");
  const campId = stateCampId || campIdFromQuery || "";

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  // Fetch training sessions for the given schedule ID
  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/Training/api/Training/GetTrainingSession?ScheduleId=${scheduleId}`,
      );
      setSessions(
        response.data.map((session) => ({
          id: session.id,
          description: session.description,
          startTime: session.exerciseStartTime,
          endTime: session.exerciseEndTime,
          type: "تمرین", // Assuming all are of type "تمرین"
          totalPlayers: session.totalPlayer,
          sessionType: session.baseActivityTypeName,
        })),
      );
    } catch (err) {
      if (err.response.data.message) {
        setError(`خطا: ${err.response.data.message}`);
      } else {
        setError("خطا در دریافت جلسات تمرینی.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [scheduleId]);

  const handleSessionAdded = () => {
    fetchSessions(); // Refresh sessions after a new one is added
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await axiosInstance.post(
        `https://api.dsfai.ir/api/Training/api/Training/DeleteTrainingSessions?Id=${sessionId}`,
      );
      setSessions(sessions.filter((session) => session.id !== sessionId)); // Remove deleted session
      setDeleteModalOpen(false); // Close the modal
    } catch (err) {
      if (err.response.data.message) {
        setError(`خطا: ${err.response.data.message}`);
      } else {
        setError("خطا در حذف تمرین.");
      }
    }
  };

  const openDeleteModal = (session) => {
    setSessionToDelete(session);
    setDeleteModalOpen(true);
  };

  if (loading) {
    return (
      <CircularProgress sx={{ display: "block", margin: "0 auto", mt: 4 }} />
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ textAlign: "center", mt: 4 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box sx={{ direction: "rtl" }}>
      {/* Page Header */}
      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          backgroundColor: "#fff",
          padding: 2,
        }}
      >
        <Grid2 container justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            جزئیات تمرین شماره {scheduleId}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setModalOpen(true)}
          >
            افزودن فعالیت جدید
          </Button>
        </Grid2>

        {/* Sessions Table */}
        <TableContainer component={Paper} sx={{ marginTop: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="right">عنوان</TableCell>
                <TableCell align="right">ساعت شروع</TableCell>
                <TableCell align="right">ساعت پایان</TableCell>
                <TableCell align="right">تعداد بازیکنان تمرین</TableCell>
                <TableCell align="right">نوع عملیات</TableCell>
                <TableCell align="right">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <TableRow key={session.id}>
                  <TableCell align="right">{session.description}</TableCell>
                  <TableCell align="right">{session.startTime}</TableCell>
                  <TableCell align="right">{session.endTime}</TableCell>
                  <TableCell align="right">{session.totalPlayers}</TableCell>
                  <TableCell align="right">{session.sessionType}</TableCell>
                  <TableCell align="right">
                    <IconButton color="primary" size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => openDeleteModal(session)} // Open delete modal
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add Session Modal */}
      <AddSessionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        scheduleId={scheduleId}
        onSessionAdded={handleSessionAdded}
        campId={campId}
      />

      {/* Delete Session Confirmation Modal */}
      <DeleteSessionModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => handleDeleteSession(sessionToDelete.id)}
        sessionDescription={sessionToDelete ? sessionToDelete.description : ""}
      />
    </Box>
  );
}
